import * as React from 'react';

import { withRouter } from 'app/context/Router';
import {
  ColumnHeader,
  ComposableTableWithFixedPageHeader,
  getButtonCellsGenerator,
  getDatestampCellGenerator,
  getTextCellGenerator,
  getUserAvatarCellGenerator,
} from 'components/ComposableTable';
import { ReachabilityInfo } from 'components/ReachabilityBadge';
import Row from 'components/Row';
import ScrollLoadZone from 'components/ScrollLoadZone';
import Text from 'components/Text';
import { PeopleTabContext, PeopleTabContextType } from 'groups/components/GroupNavigationTabs';
import EventTracking from 'lib/EventTracking';
import { RouterState } from 'lib/ReactRouterTypes';

type UserInfo = {
  id: number;
  uuid: string;
  color: string | null;
  firstName: string | null;
  lastName: string | null;
  initials: string | null;
  profilePictureUrl: string | null;
  reachabilityInfo: ReachabilityInfo;
};

export type GroupMembershipRequest = {
  uuid: string;
  user: UserInfo;
  requestMessage: string;
  createdAt: string;
};

type Props = {
  fetchMore: () => Promise<any>;
  hasNextPage: boolean;
  loading: boolean;
  groupMembershipRequests: GroupMembershipRequest[];
  onApproval: (uuid: string) => Promise<void>;
  onDenial: (uuid: string) => Promise<void>;
  router: RouterState;
  pageHeader: React.ReactElement<any>;
  pageHeaderHeight: number;
};

class PendingMembershipsTable extends React.Component<Props> {
  columnHeaderForColumn = (columnName: string): ColumnHeader => {
    const columnMap: { [name: string]: ColumnHeader } = {
      avatar: {
        name: '',
        type: 'avatar',
      },
      actions: {
        name: 'Actions',
        icon: {
          iconName: 'question-circle',
          tooltip:
            "Accepting will add them to your class, denying will notify them that they're not in the class.",
        },
      },
      name: { name: 'Name' },
      requestMessage: { name: 'Message' },
      requestSent: { name: 'Request sent' },
    };

    return columnMap[columnName];
  };

  columnHeaderNames = ['avatar', 'name', 'requestMessage', 'requestSent', 'actions'];

  columnHeaders = (): Array<ColumnHeader> => {
    return this.columnHeaderNames.map<ColumnHeader>((name: string) =>
      this.columnHeaderForColumn(name)
    );
  };

  columnGenerators = (refetch: PeopleTabContextType['refetch']) => {
    const { onApproval, onDenial } = this.props;

    // if we want typescript to get the string literals in these objects we must make them const as const.
    const buttonCell1 = {
      fieldForAction: 'uuid',
      buttonLabel: 'Approve',
      onClickAction: async (uuid: string | UserInfo) => {
        await onApproval(uuid as string);
        refetch();
      },
      buttonProps: {
        level: 'primary',
        className: 'PendingMemberships-approveButton',
        eventKey: 'request_to_join.pending_requests.pending_requests_table.approve.click',
      },
    } as const;

    const buttonCell2 = {
      fieldForAction: 'uuid',
      buttonLabel: 'Deny',
      onClickAction: async (uuid: string | UserInfo) => {
        await onDenial(uuid as string);
        refetch();
      },
      buttonProps: {
        level: 'secondary',
        className: 'PendingMemberships-denyButton',
        eventKey: 'request_to_join.pending_requests.pending_requests_table.deny.click',
      },
    } as const;

    const components: { [name: string]: (data: GroupMembershipRequest) => React.ReactElement } = {
      avatar: getUserAvatarCellGenerator({ userField: 'user' }),
      actions: getButtonCellsGenerator([buttonCell1, buttonCell2]),
      name: getTextCellGenerator(
        (row: GroupMembershipRequest) => [row.user.firstName, row.user.lastName].join(' '),
        { 'data-test-selector': 'name' }
      ),
      requestMessage: getTextCellGenerator('requestMessage', {
        'data-test-selector': 'request_message',
      }),
      requestSent: getDatestampCellGenerator('createdAt', { 'data-test-selector': 'request_sent' }),
    };

    return this.columnHeaderNames.map((name: string) => components[name]);
  };

  renderNoResultsView = () => {
    const { loading, router } = this.props;

    if (loading) {
      return null;
    }

    return (
      <div data-test="no-results-view">
        <Text align="center" size="medium">
          There are currently no requests to join your class.
        </Text>
      </div>
    );
  };

  componentDidMount() {
    EventTracking.trackKey('request_to_join.pending_requests.pending_requests_table.view');
  }
  render() {
    const {
      fetchMore,
      hasNextPage,
      loading,
      groupMembershipRequests,
      pageHeader,
      pageHeaderHeight,
    } = this.props;

    return (
      <div className="PendingMemberships-table">
        <Row size="large">
          <PeopleTabContext.Consumer>
            {peopleTabContext => {
              return (
                <ComposableTableWithFixedPageHeader
                  columnGenerators={this.columnGenerators(peopleTabContext.refetch)}
                  columnHeaders={this.columnHeaders()}
                  fixedHeader
                  keyField={(row: GroupMembershipRequest) => row.uuid}
                  loading={loading}
                  rows={groupMembershipRequests}
                  className="PendingMembershipsTable"
                  pageHeaderHeight={pageHeaderHeight}
                  pageHeader={pageHeader}
                />
              );
            }}
          </PeopleTabContext.Consumer>
        </Row>
        {!loading && hasNextPage && <ScrollLoadZone threshold={500} onZoneActivated={fetchMore} />}
        {groupMembershipRequests &&
          groupMembershipRequests.length === 0 &&
          this.renderNoResultsView()}
      </div>
    );
  }
}

export default withRouter(PendingMembershipsTable);
